import { useState, useEffect, useCallback } from 'react';

import { SPLASH_SCREEN_TIMING, SPLASH_SCREEN_SHOWN_KEY } from '@features/splashScreen/constants';
import { scheduleAnimation } from '@features/splashScreen/utils/animation';

interface AnimationState {
  showLogo: boolean;
  showTextLine1: boolean;
  showTextLine2: boolean;
  showUnderline: boolean;
  showImages: boolean;
}

interface Return {
  isVisible: boolean;
  fadeOut: boolean;
  animationState: AnimationState;
  hide: () => void;
}

export function useSplashScreen(): Return {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [fadeOut, setFadeOut] = useState<boolean>(false);
  const [showLogo, setShowLogo] = useState<boolean>(false);
  const [showTextLine1, setShowTextLine1] = useState<boolean>(false);
  const [showTextLine2, setShowTextLine2] = useState<boolean>(false);
  const [showUnderline, setShowUnderline] = useState<boolean>(false);
  const [showImages, setShowImages] = useState<boolean>(false);

  const animationState: AnimationState = {
    showLogo,
    showTextLine1,
    showTextLine2,
    showUnderline,
    showImages,
  };

  const hide = useCallback(() => {
    setFadeOut(true);
    setTimeout(() => {
      setIsVisible(false);
      try {
        sessionStorage.setItem(SPLASH_SCREEN_SHOWN_KEY, 'true');
      } catch (error) {
        console.warn('[SplashScreen] Could not set sessionStorage:', error);
      }
    }, SPLASH_SCREEN_TIMING.fadeOutDuration);
  }, []);

  const startAnimation = useCallback(() => {
    const timeouts: number[] = [];

    timeouts.push(
      scheduleAnimation(SPLASH_SCREEN_TIMING.logo, () => {
        setShowLogo(true);
      })
    );

    timeouts.push(
      scheduleAnimation(SPLASH_SCREEN_TIMING.textLine1, () => {
        setShowTextLine1(true);
      })
    );

    timeouts.push(
      scheduleAnimation(SPLASH_SCREEN_TIMING.textLine2, () => {
        setShowTextLine2(true);
      })
    );

    timeouts.push(
      scheduleAnimation(SPLASH_SCREEN_TIMING.underlineAndImages, () => {
        setShowUnderline(true);
        setShowImages(true);
      })
    );

    timeouts.push(
      scheduleAnimation(SPLASH_SCREEN_TIMING.ready, () => {
        hide();
      })
    );

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [hide]);

  useEffect(() => {
    try {
      const wasShown = sessionStorage.getItem(SPLASH_SCREEN_SHOWN_KEY);

      if (!wasShown) {
        setIsVisible(true);
        return startAnimation();
      }
    } catch (error) {
      console.warn('[SplashScreen] SessionStorage not available:', error);
    }
  }, [startAnimation]);

  return {
    isVisible,
    fadeOut,
    animationState,
    hide,
  } satisfies Return;
}
